import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TablePagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Alert
} from '@mui/material';
import { Add, Edit, Delete, Search, Visibility, VisibilityOff } from '@mui/icons-material';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import { pricingAPI, subUserAPI } from '../../services/api';

const SubUserManagement = () => {
  const [subUsers, setSubUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    selectedServices: [],
    otp: ''
  });
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false);

  useEffect(() => {
    fetchServices();
    fetchSubUsers();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await pricingAPI.getAll();
      if (response.success) {
        setServices(response.data);
      }
    } catch (error) {
      // console.error('Error fetching services:', error);
      toast.error('Failed to load services');
    }
  };

  const fetchSubUsers = async () => {
    setLoading(true);
    try {
      const response = await subUserAPI.getAll();
      setSubUsers(response.data || []);
    } catch (error) {
      // console.error('Error fetching sub-users:', error);
      toast.error('Failed to load sub-users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (user = null) => {
    if (user) {
      setEditingUser(user);
      
      // Fetch full sub-user details including permissions
      try {
        const response = await subUserAPI.getDetails(user.id);
        if (response.success) {
          const userData = response.data;
          setFormData({
            firstName: userData.firstName,
            lastName: userData.lastName,
            email: userData.email,
            phone: userData.phone,
            password: '',
            confirmPassword: '',
            selectedServices: userData.selectedServices || [],
            otp: ''
          });
        }
      } catch (error) {
        // console.error('Error fetching sub-user details:', error);
        toast.error('Failed to load sub-user details');
        // Fallback to basic user data
        setFormData({
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          phone: user.phone,
          password: '',
          confirmPassword: '',
          selectedServices: [],
          otp: ''
        });
      }
      
      setOtpSent(false);
      setOtpVerified(false);
    } else {
      setEditingUser(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        selectedServices: [],
        otp: ''
      });
      setOtpSent(false);
      setOtpVerified(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      selectedServices: [],
      otp: ''
    });
    setOtpSent(false);
    setOtpVerified(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      selectedServices: prev.selectedServices.includes(serviceId)
        ? prev.selectedServices.filter(id => id !== serviceId)
        : [...prev.selectedServices, serviceId]
    }));
  };

  const handleSendOTP = async () => {
    if (!formData.email || !formData.firstName) {
      toast.error('Please enter email and first name');
      return;
    }

    setSendingOTP(true);
    try {
      await subUserAPI.sendOTP(formData.email, formData.firstName);
      setOtpSent(true);
      toast.success('OTP sent to email successfully');
    } catch (error) {
      // console.error('Error sending OTP:', error);
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setSendingOTP(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!formData.otp || formData.otp.length !== 4) {
      toast.error('Please enter a valid 4-digit OTP');
      return;
    }

    try {
      await subUserAPI.verifyOTP(formData.email, formData.otp);
      setOtpVerified(true);
      toast.success('OTP verified successfully');
    } catch (error) {
      // console.error('Error verifying OTP:', error);
      toast.error(error.response?.data?.message || 'Invalid OTP');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!editingUser) {
      if (!otpVerified) {
        toast.error('Please verify OTP first');
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
    }

    if (formData.selectedServices.length === 0) {
      toast.error('Please select at least one service');
      return;
    }

    try {
      if (editingUser) {
        await subUserAPI.update(editingUser.id, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: formData.phone,
          selectedServices: formData.selectedServices
        });
        toast.success('Sub-user updated successfully');
      } else {
        await subUserAPI.create({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phone,
          password: formData.password,
          selectedServices: formData.selectedServices,
          otp: formData.otp
        });
        toast.success('Sub-user created successfully');
      }
      
      handleCloseDialog();
      fetchSubUsers();
    } catch (error) {
      // console.error('Error saving sub-user:', error);
      toast.error(error.response?.data?.message || 'Failed to save sub-user');
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this sub-user? This action cannot be undone.')) {
      try {
        await subUserAPI.delete(userId);
        toast.success('Sub-user deleted successfully');
        fetchSubUsers();
      } catch (error) {
        // console.error('Error deleting sub-user:', error);
        toast.error(error.response?.data?.message || 'Failed to delete sub-user');
      }
    }
  };

  const filteredUsers = subUsers.filter(user =>
    user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 3, sm: 4 }, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
              Sub-User Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
              Create and manage sub-users with limited service access
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Add Sub-User
          </Button>
        </Box>

        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
          <TextField
            fullWidth
            label="Search"
            placeholder="Search by name or email"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
        </Paper>

        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>#</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Full Name</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Email</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Phone</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Services Assigned</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Created At</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : paginatedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {searchTerm ? 'No sub-users found matching your search' : 'No sub-users created yet'}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((user, index) => (
                  <TableRow key={user.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{user.firstName} {user.lastName}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{user.email}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{user.phone || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Chip label={`${user.servicesCount} Services`} color="primary" size="small" />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Chip 
                        label={user.isActive ? 'Active' : 'Inactive'}
                        color={user.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton size="small" color="primary" onClick={() => handleOpenDialog(user)}>
                        <Edit />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(user.id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
          />
        </TableContainer>

        {/* Add/Edit Sub-User Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingUser ? 'Edit Sub-User' : 'Add New Sub-User'}
          </DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={!!editingUser || otpSent}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </Grid>

                {!editingUser && (
                  <>
                    <Grid item xs={12}>
                      <Alert severity="info">
                        Email verification is required. Click "Send OTP" to receive a 4-digit code.
                      </Alert>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleSendOTP}
                        disabled={!formData.email || !formData.firstName || otpSent || sendingOTP}
                      >
                        {sendingOTP ? 'Sending...' : otpSent ? 'OTP Sent' : 'Send OTP'}
                      </Button>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Enter 4-Digit OTP"
                        name="otp"
                        value={formData.otp}
                        onChange={handleChange}
                        disabled={!otpSent || otpVerified}
                        inputProps={{ maxLength: 4 }}
                        InputProps={{
                          endAdornment: otpVerified ? (
                            <Chip label="Verified" color="success" size="small" />
                          ) : otpSent ? (
                            <Button size="small" onClick={handleVerifyOTP}>
                              Verify
                            </Button>
                          ) : null
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        disabled={!otpVerified}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Confirm Password"
                        type={showPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        disabled={!otpVerified}
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Assign Services
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Select which verification services this sub-user can access. Credits will be deducted from your account.
                </Alert>
                <FormGroup>
                  <Grid container spacing={1}>
                    {services.map(service => (
                      <Grid item xs={12} sm={6} key={service.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.selectedServices.includes(service.id)}
                              onChange={() => handleServiceToggle(service.id)}
                            />
                          }
                          label={`${service.name} (₹${service.price})`}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              disabled={!editingUser && !otpVerified}
            >
              {editingUser ? 'Update' : 'Create'} Sub-User
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default SubUserManagement;
